export interface LoginFormData {
  phoneNumber: string;
  captchaAnswer: string;
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  acceptPersonalData: boolean;
  acceptPlatformTerms: boolean;
}

export interface LoginPageProps {
  formData: LoginFormData;
  isLoading: boolean;
  error: string | null;
  onInputChange: (field: keyof LoginFormData, value: string | boolean) => void;
  onSMSLogin: () => void;
  onMTSCLogin: () => void;
  onFNSLogin: () => void;
  onToggleTerms: () => void;
  onToggleOtherMethods: () => void;
  onVerifySMSCode: (code: string) => Promise<void>;
  onResetCaptcha: () => void;
}

// Новые типы для авторизации
export enum AuthResult {
  VerifyCode = 'VerifyCode',
  WrongCode = 'WrongCode',
  Authorized = 'Authorized',
  NeedPhone = 'NeedPhone'
}

export interface ResponseAuth {
  result: AuthResult;
  accessToken?: string;
  refreshToken?: string;
  url?: string;
}

export type AuthSource = 'Internal' | 'MtsId';

export interface AuthRequest {
  phone: number;
  source: AuthSource;
}

export interface VerifyRequest {
  phone: number;
  code: string;
}

// Enum для статуса пользователя
export enum UserStatus {
  Blocked = 'Blocked',
  Temp = 'Temp',
  Anonymous = 'Anonymous',
  Verified = 'Verified',
  Selfemployed = 'Selfemployed'
}

// Enum для статуса сделки
export enum DealStatus {
  New = 'New',           // создана продавцом: нет receiptEmail и других проверок, не отправлена в Tochka
  Registered = 'Registered', // покупатель установил receiptEmail & сделка зарегистрирована в Tochka
  Deleted = 'Deleted',   // продавец удалил до получения статуса 'Registered'
  Wait = 'Wait',         // в процессе
  Paid = 'Paid',         // оплачена покупателем
  Finished = 'Finished', // успешно завершена
  Rejected = 'Rejected', // отклонена или истекло время
  Refunded = 'Refunded'  // возвращена продавцом
}

// Интерфейс для банковской карты
export interface CardDTO {
  id: string; // Guid в C# -> string в TypeScript
  maskedCardNumber: string;
}

// Интерфейс для ответа при добавлении карты
export interface AddCardResponse {
  url?: string; // URL для перехода (если задан)
}

// Интерфейс для истории сделки
export interface DealHistoryItem {
  createdAt: string;             // DateTime в C# -> string в TypeScript
  action: string;                // описание действия
}

export interface FnsAuthResult {
  challengeToken?: string;
  message?: string;
}

// Интерфейс для сделки
export interface DealDTO {
  id: string;                    // Guid в C# -> string в TypeScript
  amount: number;                // long в C# -> number в TypeScript
  purpose: string;               // назначение платежа
  receiptEmail?: string;         // email для чека (устанавливает покупатель)
  createdAt: string;             // DateTime в C# -> string в TypeScript
  status: DealStatus;            // статус сделки
  card?: CardDTO;                // банковская карта (опционально)
  platformCommissionBill?: string; // наш счет от CloudPayments
  npdBill?: string;              // чек НПД
  platformCommission: number;    // наша комиссия (long в C# -> number в TypeScript)
  isFnsEnabled: boolean;         // включен ли ФНС
  history?: DealHistoryItem[];   // история сделки (опционально)
  paidAt?: string;               // когда оплачена
  payerConfirmedAt?: string;
  payerRejectedAt?: string;
  recipientConfirmedAt?: string;
  recipientRejectedAt?: string;
}

// Информация для страницы чека (подтверждение/отмена покупателем)
export interface DealReceiptInfoDTO {
  id: string;
  purpose: string;
  amount: number;
  status: DealStatus;
  canConfirm: boolean;
  canCancel: boolean;
  /** Токен для подтверждения/отмены (возвращается при заходе без token в URL). */
  payerConfirmToken?: string | null;
}

// Интерфейс для создания сделки
export interface CreateDealRequest {
  amount: number;                // сумма в копейках
  cardId: string;                // Guid в C# -> string в TypeScript
  purpose: string;               // назначение платежа
  isFnsEnabled: boolean;         // включен ли ФНС
}

// Интерфейс для персональных данных
export interface PersonalData {
  // common
  fullName?: string; // ФИО
  birthDate?: string; // DateOnly в C# -> string в TypeScript

  // from passport services
  passportSeries?: string;
  passportNumber?: string;
  passportIssuedBy?: string;
  passportIssueDate?: string; // DateOnly в C# -> string в TypeScript
  passportAddress?: string;
  passportPlaceOfBirth?: string;
  passportVerifiedAt?: string; // DateTime в C# -> string в TypeScript
  passportPoliceDepartmentCode?: string;
  passportPassportPlaceOfBirth?: string;

  // from FNS
  INN?: string;
  SNILS?: string;
}

// Обновленный интерфейс User
export interface User {
  id: string; // Guid в C# -> string в TypeScript
  createdAt: string; // DateTime в C# -> string in TypeScript
  phone: number; // long в C# -> number в TypeScript
  status: UserStatus;
  
  // personal data
  personalData?: PersonalData;
  isFnsEnabled: boolean;
  
  // finances
  commission: number; // Commission в C# -> commission в TypeScript (camelCase)
  
  // banking
  cards?: CardDTO[];
}

// Экспортируем API типы
export * from './api';
